import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChartNotePatientHandoutMode } from '@models';
import { ChartNotePatientHandoutService, ToastService } from '@services';
import { ButtonComponent, SpinnerComponent } from '@ui-lib';

export interface PatientHandoutDialogData {
  patientId: string;
  chartNoteId: string;
  patientName?: string;
}

interface HandoutOption {
  value: ChartNotePatientHandoutMode;
  label: string;
  description: string;
  disabled?: boolean;
}

type HandoutStep = 'select' | 'generating' | 'preview';

@Component({
  selector: 'provider-portal-patient-handout-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, SpinnerComponent],
  templateUrl: './patient-handout-dialog.component.html',
})
export class PatientHandoutDialogComponent {
  private readonly dialogRef = inject<DialogRef<boolean>>(DialogRef);
  private readonly data = inject<PatientHandoutDialogData>(DIALOG_DATA);
  private readonly handout = inject(ChartNotePatientHandoutService);
  private readonly toast = inject(ToastService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly options: HandoutOption[] = [
    {
      value: 'VisitSummary',
      label: 'Visit Summary',
      description:
        "Plain-language summary of the patient's concerns, progress, and key findings",
    },
    {
      value: 'CarePlan',
      label: 'Care Plan & Recommendations',
      description:
        'Nutrition, lifestyle, supplement, and medication instructions documented in the plan',
    },
    {
      value: 'VisitSummaryAndCarePlan',
      label: 'Both in one document',
      description: '',
    },
  ];

  protected readonly step = signal<HandoutStep>('select');
  protected readonly selected =
    signal<ChartNotePatientHandoutMode>('VisitSummary');
  protected readonly sharing = signal(false);
  protected readonly previewHtml = signal<SafeHtml | null>(null);

  protected select(value: ChartNotePatientHandoutMode): void {
    this.selected.set(value);
  }

  protected create(): void {
    this.step.set('generating');
    this.handout
      .generate(this.data.patientId, this.data.chartNoteId, this.selected())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.previewHtml.set(
            this.sanitizer.bypassSecurityTrustHtml(result.html)
          );
          this.step.set('preview');
        },
        error: () => {
          this.toast.showWarning(
            'Could not generate the handout. Please try again.'
          );
          this.step.set('select');
        },
      });
  }

  protected share(): void {
    if (this.sharing()) {
      return;
    }
    this.sharing.set(true);
    this.handout
      .share(this.data.patientId, this.data.chartNoteId, this.selected())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toast.showSuccess('Handout shared with the patient.');
          this.dialogRef.close(true);
        },
        error: () => {
          this.toast.showWarning('Failed to share the handout.');
          this.sharing.set(false);
        },
      });
  }

  protected close(): void {
    this.dialogRef.close(false);
  }
}
